import apiSlice from '../api/api.slice';
import {
  ICommentThread,
  IComment,
  ICreateCommentRequest,
  IDeleteCommentRequest,
  IProjectGroupComment,
  ICreateProjectGroupCommentRequest,
  IDeleteProjectGroupCommentRequest,
} from './comment';

const commentApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getComments: builder.query<ICommentThread[], string>({
      query: (postId) => ({
        url: `/api/v1/comment/getCommentsByPostId/${postId}`,
        method: 'GET',
      }),
      transformResponse: (response: { comments: ICommentThread[] }) =>
        response.comments,
    }),
    createComment: builder.mutation<IComment, ICreateCommentRequest>({
      query: ({ postId, comment, parentCommentId }) => ({
        url: `/api/v1/comment/create`,
        method: 'POST',
        body: {
          postId,
          comment,
          ...(parentCommentId ? { parentCommentId } : {}),
        },
      }),
      transformResponse: (response: { comment: IComment }) => response.comment,
    }),
    deleteComment: builder.mutation<IComment, IDeleteCommentRequest>({
      query: (params) => ({
        url: `/api/v1/comment/delete`,
        method: 'DELETE',
        params,
      }),
    }),
    toggleCommentResolution: builder.mutation<
      void,
      { commentId: string; token: string }
    >({
      query: ({ commentId, token }) => ({
        url: `/api/v1/comment/admin/toggleResolution/${commentId}?token=${token}`,
        method: 'POST',
      }),
    }),
    // Project Group Comment Endpoints
    getProjectGroupComments: builder.query<ICommentThread[], string>({
      query: (projectGroupId) => ({
        url: `/api/v1/comment/project-group/getCommentsByProjectGroupId/${projectGroupId}`,
        method: 'GET',
      }),
      transformResponse: (response: { comments: ICommentThread[] }) =>
        response.comments,
    }),
    createProjectGroupComment: builder.mutation<
      IProjectGroupComment,
      ICreateProjectGroupCommentRequest
    >({
      query: ({ projectGroupId, comment, parentCommentId }) => ({
        url: `/api/v1/comment/project-group/create`,
        method: 'POST',
        body: {
          projectGroupId,
          comment,
          ...(parentCommentId ? { parentCommentId } : {}),
        },
      }),
      transformResponse: (response: { comment: IProjectGroupComment }) =>
        response.comment,
    }),
    deleteProjectGroupComment: builder.mutation<
      IProjectGroupComment,
      IDeleteProjectGroupCommentRequest
    >({
      query: (params) => ({
        url: `/api/v1/comment/project-group/delete`,
        method: 'DELETE',
        params,
      }),
    }),
  }),
});

apiSlice.enhanceEndpoints({
  addTagTypes: [
    'Comment',
    'ProjectGroupComment',
    'Feed',
    'UserPosts',
    'ProjectGroupList',
    'PostActivity',
  ],
  endpoints: {
    getComments: {
      providesTags: ['Comment'],
    },
    createComment: {
      invalidatesTags: ['Comment', 'Post', 'Feed', 'UserPosts'],
    },
    deleteComment: {
      invalidatesTags: ['Comment', 'Post', 'Feed', 'UserPosts'],
    },
    toggleCommentResolution: {
      invalidatesTags: ['Post', 'PostActivity'],
    },
    getProjectGroupComments: {
      providesTags: ['ProjectGroupComment'],
    },
    createProjectGroupComment: {
      invalidatesTags: [
        'ProjectGroupComment',
        'ProjectGroup',
        'ProjectGroupList',
        'Feed',
        'UserPosts',
      ],
    },
    deleteProjectGroupComment: {
      invalidatesTags: [
        'ProjectGroupComment',
        'ProjectGroup',
        'ProjectGroupList',
        'Feed',
        'UserPosts',
      ],
    },
  },
});

export const {
  useGetCommentsQuery,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useToggleCommentResolutionMutation,
  useGetProjectGroupCommentsQuery,
  useCreateProjectGroupCommentMutation,
  useDeleteProjectGroupCommentMutation,
} = commentApiSlice;
